<template>
  <controller :lockScroll="true" :goToTopButton="false">
    <section class="nivvo-embed-page">
      <div v-if="loading || error" :class="['nivvo-state', { error }]" role="status" aria-live="polite">
        <span v-if="loading" class="loader" aria-hidden="true"></span>
        <p>{{ message }}</p>
        <button v-if="error" type="button" @click="startHandoff()">Tentar novamente</button>
      </div>
      <iframe
        v-else
        ref="nivvoFrame"
        :src="redirectUrl"
        title="Nivvo"
        allow="clipboard-read; clipboard-write"
        @load="onIframeLoad"
      ></iframe>
    </section>
  </controller>
</template>

<script>
import NivvoService from '@/services/nivvo.service'

export default {
  name: 'NivvoEmbed',
  data () {
    return {
      error: null,
      loading: true,
      message: 'Abrindo Nivvo...',
      redirectUrl: null,
      embedOrigin: null,
      iframeReady: false,
      heartbeatTimer: null,
      reauthenticating: false,
      routeSyncedFromEmbed: null
    }
  },
  watch: {
    '$route.fullPath' (fullPath) {
      if (this.routeSyncedFromEmbed === fullPath) {
        this.routeSyncedFromEmbed = null
        return
      }

      const redirect = this.redirectTo()
      if (this.iframeReady && this.embedOrigin) {
        this.sendIframeMessage('nivvo:navigate', { href: redirect })
        return
      }
      this.startHandoff(redirect)
    }
  },
  mounted () {
    window.addEventListener('message', this.onEmbedMessage)
    this.startHandoff()
  },
  beforeUnmount () {
    this.stopHeartbeat()
    window.removeEventListener('message', this.onEmbedMessage)
  },
  methods: {
    startHandoff (redirectOverride = null) {
      this.stopHeartbeat()
      this.error = null
      this.loading = true
      this.message = 'Abrindo Nivvo...'
      this.redirectUrl = null
      this.embedOrigin = null
      this.iframeReady = false

      NivvoService.redirectUrl({ redirectTo: this.redirectFromHref(redirectOverride) || this.redirectTo() }).then(
        redirectUrl => {
          try {
            const parsed = new URL(redirectUrl)
            if (parsed.protocol !== 'https:' && parsed.hostname !== 'localhost') throw new Error('Origem do Nivvo não é segura.')
            this.embedOrigin = parsed.origin
            this.redirectUrl = redirectUrl
            this.loading = false
          } catch (error) {
            this.showHandoffError(error)
          }
        },
        error => this.showHandoffError(error)
      )
    },
    showHandoffError (error) {
      this.error = error
      this.loading = false
      this.message = error.response?.data?.message || error.message || 'Não foi possível abrir o Nivvo.'
      window.toaster && window.toaster.emit('open', { style: 'warning', message: this.message, floater: true })
    },
    redirectTo () {
      const pathMatch = this.$route.params.pathMatch
      const path = Array.isArray(pathMatch) ? pathMatch.join('/') : pathMatch
      const pathname = path ? `/${path}` : '/my-work'
      const params = new URLSearchParams()

      Object.keys(this.$route.query || {}).forEach(key => {
        if (key === 'ticket' || key === 'embed') return
        const values = Array.isArray(this.$route.query[key]) ? this.$route.query[key] : [this.$route.query[key]]
        values.forEach(value => {
          if (value !== undefined && value !== null && value !== '') params.append(key, value)
        })
      })

      const query = params.toString()
      return `${pathname}${query ? `?${query}` : ''}${this.$route.hash || ''}`
    },
    onIframeLoad () {
      this.startHeartbeat()
      this.sendIframeMessage('nivvo:keepalive')
    },
    startHeartbeat () {
      this.stopHeartbeat()
      this.heartbeatTimer = window.setInterval(() => this.sendIframeMessage('nivvo:keepalive'), 5 * 60 * 1000)
    },
    stopHeartbeat () {
      if (this.heartbeatTimer) window.clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    },
    sendIframeMessage (type, payload = {}) {
      if (!this.embedOrigin) return
      this.$refs.nivvoFrame?.contentWindow?.postMessage({ type, ...payload }, this.embedOrigin)
    },
    onEmbedMessage (event) {
      if (event.source !== this.$refs.nivvoFrame?.contentWindow || event.origin !== this.embedOrigin) return
      const payload = event.data || {}
      if (payload.source !== 'nivvo') return

      if ((payload.type === 'nivvo:ready' || payload.type === 'nivvo:navigation') && payload.href) {
        const redirect = this.redirectFromHref(payload.href)
        if (redirect) {
          this.iframeReady = true
          this.syncApolloRoute(redirect)
        }
      }
      if (payload.type === 'nivvo:session-expired') this.reauthenticate(payload.href)
    },
    syncApolloRoute (redirect) {
      const url = new URL(redirect, this.embedOrigin || window.location.origin)
      if (url.pathname.startsWith('/api/') || url.pathname.startsWith('/auth/')) return
      const apolloPath = `/nivvo${url.pathname === '/' ? '/my-work' : url.pathname}${url.search}${url.hash}`
      if (apolloPath === this.$route.fullPath) return

      this.routeSyncedFromEmbed = apolloPath
      this.$router.replace(apolloPath).catch(error => {
        this.routeSyncedFromEmbed = null
        if (error?.name !== 'NavigationDuplicated') console.error('Falha ao sincronizar rota do Nivvo.', error)
      })
    },
    reauthenticate (href) {
      if (this.reauthenticating) return
      this.reauthenticating = true
      this.startHandoff(href)
      window.setTimeout(() => { this.reauthenticating = false }, 1500)
    },
    redirectFromHref (href) {
      if (!href || typeof href !== 'string') return null
      try {
        const url = new URL(href, this.embedOrigin || window.location.origin)
        if (this.embedOrigin && url.origin !== this.embedOrigin) return null
        if (url.pathname.startsWith('/api/') || url.pathname.startsWith('/auth/')) return null
        const redirect = `${url.pathname}${url.search}${url.hash}`
        return redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : null
      } catch {
        return null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.nivvo-embed-page {
  background-color: $color-bg;
  display: flex;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  width: 100%;

  iframe { border: 0; display: block; flex: 1; height: 100%; min-height: 0; width: 100%; }
}

.nivvo-state {
  align-items: center;
  color: $color-text;
  display: flex;
  flex: 1;
  flex-direction: column;
  font-weight: 600;
  gap: $hmg_small;
  justify-content: center;
  padding: $hmg;
  text-align: center;

  &.error { color: $color-alert; }
  p { margin: 0; }
  button { min-height: 44px; padding: 0 $hmg; }
}
</style>
